import React from "react";
import {
  CONTENT_TYPES,
  PAGES,
  SECTIONS_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { Columns, Toast } from "@gonoodle/gn-universe-ui";
import { dehydrate, QueryClient } from "@tanstack/react-query";

import api from "../api";
import { useFavoritesQuery } from "../hooks";
import Layout from "../components/Layout";
import PrintableTile from "../components/PrintableTile";
import VideoTile from "../components/VideoTile";
import { QUERY_KEYS, ROUTE_PERMISSIONS } from "../constants";
import { withAuthorization } from "../containers";
import PillTabs from "../components/PillTabs";

export default function FavoritesPage() {
  const { favorites, isFavoritesLoading, favoritesError } = useFavoritesQuery();

  const alert = Toast.useAlert();
  const videos = [];
  const printables = [];
  favorites?.forEach((item) => {
    if (item.type === CONTENT_TYPES.VIDEO) {
      videos.push(item.activity);
    } else if (item.type === CONTENT_TYPES.ACTIVITY) {
      printables.push(item.activity);
    }
  });

  if (favoritesError) {
    alert.error("Something went wrong. Please reload the page");
  }

  return (
    <Layout
      pageTitle="Favorites"
      pageDescription="A list of all your favorites!"
      progressBar
    >
      <div className="px-sm">
        <h1 className="gn-headline text-xl md:text-2xl text-white text-center mb-lg mt-xl">
          Favorites
        </h1>
        {!favoritesError ? (
          <PillTabs defaultTab={0}>
            <div label="Videos" role="tabpanel">
              {videos.length === 0 ? (
                <p className="text-center mt-lg text-gray-500">
                  You have no favorited videos.
                </p>
              ) : (
                <Columns loading={isFavoritesLoading} cols={4}>
                  {videos.map((video) => (
                    <li key={video.id}>
                      <VideoTile
                        id={video.id}
                        slug={video.slug}
                        title={video.title}
                        description={video.description}
                        images={video.images}
                        duration={video.duration}
                        favorite={video.favorite}
                        sponsor={{
                          expiration: video.attribution?.expiration,
                          type: video.attribution?.type,
                        }}
                        linkParams={{
                          sourceName: SECTIONS_TYPES.FAVORITES,
                          sourceElement: SECTIONS_TYPES.FAVORITES,
                          sourcePage: PAGES.FAVORITES,
                          sourcePageType: PAGES.FAVORITES,
                        }}
                      />
                    </li>
                  ))}
                </Columns>
              )}
            </div>
            <div label="Activities" role="tabpanel">
              {printables.length === 0 ? (
                <p className="text-center mt-lg text-gray-500">
                  You have no favorited activities.
                </p>
              ) : (
                <Columns loading={isFavoritesLoading} cols={5}>
                  {printables.map(({ id, slug, title, image, favorite }) => (
                    <li
                      className="relative group"
                      key={`printable-${id}-${title}`}
                    >
                      <PrintableTile
                        id={id}
                        slug={slug}
                        mode="dark"
                        sources={image}
                        title={title}
                        favorite={favorite}
                        linkParams={{
                          sourceName: SECTIONS_TYPES.FAVORITES,
                          sourceElement: SECTIONS_TYPES.FAVORITES,
                          sourcePage: PAGES.FAVORITES,
                          sourcePageType: PAGES.FAVORITES,
                        }}
                      />
                    </li>
                  ))}
                </Columns>
              )}
            </div>
          </PillTabs>
        ) : null}
      </div>
    </Layout>
  );
}

export const getServerSideProps = withAuthorization(async (ctx) => {
  const fetcher = api(ctx);
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery([QUERY_KEYS.FAVORITES], () =>
    fetcher.getFavorites(),
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
}, ROUTE_PERMISSIONS.PRIVATE);
